/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.patients

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.UserDataRepository
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.model.patient.Patient
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.patients.generated.resources.Res
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_load_failed

data class PatientListItemDisplay(
    val patient: Patient,
    val defaultFeeDisplay: String,
)

data class PatientListState(
    val patients: List<Patient> = emptyList(),
    val patientsDisplay: List<PatientListItemDisplay> = emptyList(),
    val isLoading: Boolean = true,
    val error: UiText? = null,
)

sealed class PatientListEvent

sealed class PatientListAction {
    data object Refresh : PatientListAction()
}

class PatientListViewModel(
    private val patientRepository: PatientRepository,
    private val userDataRepository: UserDataRepository,
    private val currencyFormatter: CurrencyFormatter,
) : BaseViewModel<PatientListState, PatientListEvent, PatientListAction>(PatientListState()) {

    init {
        loadPatients()
    }

    override fun handleAction(action: PatientListAction) {
        when (action) {
            PatientListAction.Refresh -> loadPatients()
        }
    }

    private fun loadPatients() {
        mutableStateFlow.value = state.copy(isLoading = true, error = null)
        viewModelScope.launch {
            combine(
                patientRepository.getPatients(),
                userDataRepository.observeDisplayPreferences,
            ) { patients, _ -> patients }
                .catch { e ->
                    mutableStateFlow.value = state.copy(
                        isLoading = false,
                        error = e.toUiText(Res.string.patients_error_load_failed),
                    )
                }
                .collect { patients ->
                    mutableStateFlow.value = state.copy(
                        patients = patients,
                        patientsDisplay = patients.map { p ->
                            PatientListItemDisplay(
                                patient = p,
                                defaultFeeDisplay = currencyFormatter.format(p.defaultFee),
                            )
                        },
                        isLoading = false,
                    )
                }
        }
    }
}
